import { useState, useEffect, useMemo, useRef } from "react";
import { MicVAD } from "@ricky0123/vad-web";
import { speechToText, textToSpeech } from "./api";
import {
  VOICE_CONSENT_VERSION, hasValidVoiceConsent,
  hasVoiceEntitlement, getVoiceLimitSeconds, resolveVoiceBalance,
} from "./constants";
import { LANGUAGE_NAME_TO_BCP47 } from "./tts";

// ─────────────────────────────────────────────────────────────────────────────
// useVoiceTurn — the shared voice I/O pipeline (VAD capture, STT, the A4 review/
// grace-period window, TTS playback, voice-minutes billing, and the A5 per-tab
// mic mutex). Extracted verbatim from AgentChat.js so more than one surface (the
// Babi chat widget today; the planned Listen & Speak Lab) can drive the exact
// same underlying machine while configuring the parts that legitimately differ:
//   - silenceTimeoutMs : the VAD `redemptionMs` (how long after speech-negative
//     frames the turn is treated as finished). The widget wants a snappy cutoff;
//     the Lab will want a longer one.
//   - ttsEnabled       : whether speakReply() actually synthesizes/plays audio.
//   - buildSystemPrompt : passed straight through to onSubmitTranscript so the
//     consumer's reply phase can build the prompt it wants.
//   - onSubmitTranscript : the consumer-owned "a confirmed transcript is ready,
//     go produce a reply" step. The hook never calls the model itself.
// Everything else in here is byte-for-byte the widget's original behavior.
// ─────────────────────────────────────────────────────────────────────────────

// Independent of the per-consumer silence timeout (redemptionMs, passed in) — that
// one only starts counting once speech has already begun (confirmed from
// @ricky0123/vad-web's own source: its redemption countdown is gated behind
// `this.speaking`, which never becomes true if no speech is ever detected). This
// constant instead bounds how long we'll wait for speech to START at all after a
// capture turn begins, via a separate setTimeout — a user who taps the mic and
// says nothing would otherwise leave it listening indefinitely. Shared by both
// consumers (widget + Lab); not overridable per-consumer.
const MAX_SILENT_START_MS = 3000;

// A freshly-opened mic delivers ~200-400ms of silence / attenuated audio while the OS capture
// pipeline and the browser's auto-gain settle. Two mitigations, both shared by widget + Lab:
//  - MIC_WARMUP_MS: hold on "starting…" for this long AFTER the VAD is ready before flipping to
//    "capturing" ("Listening…"), so a user who speaks the instant they're told to isn't racing
//    a cold mic. The VAD is already running and buffering during this window — nothing is lost;
//    it's purely when we *say* we're ready.
//  - PRE_SPEECH_PAD_MS: how much audio BEFORE speech-detection the VAD keeps and prepends to the
//    captured segment. vad-web's default is 800ms; bumped so the first word or two (spoken just
//    before the Silero model crosses its threshold, or during the mic ramp) survives. Google STT
//    bills the extra leading audio (~a few hundredths of a cent) and transcribes leading silence
//    without issue.
const MIC_WARMUP_MS = 350;
const PRE_SPEECH_PAD_MS = 1500;

// A4: how long a just-transcribed voice turn sits editable before auto-sending if
// left untouched. Cancelled atomically the moment editing begins — see
// beginEditingPendingVoiceText's comment for exactly how that's guaranteed.
export const TRANSCRIPT_GRACE_PERIOD_MS = 5000;

// A5: how stale an activeVoiceSession marker has to be before another tab is allowed to
// ignore it and start capturing anyway — self-healing against a crashed/closed tab that
// never got to clear its own marker, without needing any explicit cleanup-on-disconnect
// mechanism (this app has no realtime presence infrastructure to hook into).
const VOICE_SESSION_STALE_MS = 90000;

// One per page load/tab, not per component instance or render — identifies which tab's
// activeVoiceSession marker is "ours" so we don't warn ourselves.
const VOICE_SESSION_TAB_ID = Math.random().toString(36).slice(2);

// Same-tab single-flight for mic capture. The A5 activeVoiceSession / VOICE_SESSION_TAB_ID
// guard above is for the CROSS-tab race and structurally cannot see a second useVoiceTurn
// instance in the SAME tab (same module load → same VOICE_SESSION_TAB_ID → the guard's
// `tabId !== VOICE_SESSION_TAB_ID` is always false). This module-level holder closes that
// gap: both instances share this JS runtime, so a plain identity token is enough. Set on
// capture-start, cleared on every capture-end path; a second same-tab capture attempt while
// it's held fails cleanly into the existing 'device-in-use' state instead of running both
// mics concurrently (double STT billing, two replies). Only the chat widget + the Listen &
// Speak Lab ever mount this hook, and only one at a time should hold the mic.
let sameTabMicHolder = null;

// Parses Google's billed-duration string shape ("1s", "1.500s") into a plain number of
// seconds. Returns 0 for anything unparseable rather than throwing — a missing/malformed
// duration should mean "nothing to bill", never a crash.
function parseGoogleDurationSeconds(value) {
  if (!value) return 0;
  const match = /^([\d.]+)s$/.exec(value);
  return match ? parseFloat(match[1]) : 0;
}

// Converts VAD's captured speech segment (Float32, -1..1, 16kHz mono) into
// base64-encoded LINEAR16 PCM for Google STT's `audio.content` field.
// Chunked String.fromCharCode.apply to stay well under browsers' per-call
// argument-count limits on longer utterances.
function floatTo16BitPCMBase64(float32Array) {
  const buffer = new ArrayBuffer(float32Array.length * 2);
  const view = new DataView(buffer);
  for (let i = 0, offset = 0; i < float32Array.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

export function useVoiceTurn({
  profile,
  subscription,
  isAdminAccount,
  isSimpleViewer,
  onUpdateProfile,
  // config
  panelOpen = false,          // gates the background VAD preload (widget: the panel's `open`)
  enablePreload = true,       // widget: true; Lab: false (cold start each session is acceptable)
  silenceTimeoutMs,           // VAD redemptionMs — widget: WIDGET_SILENCE_TIMEOUT_MS
  ttsEnabled = false,         // widget: BABI_WIDGET_TTS_ENABLED (false)
  autoSendPendingReview = true, // widget: true — the A4 review bubble auto-sends after
                              // TRANSCRIPT_GRACE_PERIOD_MS if left untouched. Lab: false —
                              // the learner confirms every turn explicitly, the bubble never
                              // self-sends (no timer is ever scheduled). Nothing else changes:
                              // the pending-review state, editing, confirm and discard/redo
                              // paths are identical either way.
  languageCode,               // BCP-47 for BOTH STT recognition and TTS synthesis. Omit to
                              // fall back to the base-language code (the widget's behaviour);
                              // the Lab passes the TARGET-language code (Turkish) instead.
  ttsVoice,                   // optional explicit Google voice name for TTS. Omit → proxy
                              // picks the best available for the language (the widget). The
                              // Lab passes its own dedicated Chirp3-HD voice.
  ttsRate,                    // optional Google speakingRate (0.25-2.0) for TTS — the Lab's
                              // speed control. Omit → the proxy's own default (native 1.0
                              // speed), same as every render before this option existed. The
                              // widget never passes this. Read fresh from this render's props
                              // each time speakReply is called (this hook isn't memoized, and
                              // speakReply isn't wrapped in useCallback), so the LIVE reply
                              // (the synthesis call itself) is generated at whatever rate was
                              // active when that call started — never retroactively. speakReply
                              // returns the rate it used alongside the audio precisely so a
                              // caller that caches the clip (the Lab's "Listen again") can later
                              // re-derive the correct playbackRate against a DIFFERENT toggle
                              // value, without a new synthesis call — see ListenSpeakLab's
                              // turnAudio/playTurnAudio.
  buildSystemPrompt,          // fn(isVoiceTurn) => string — forwarded to onSubmitTranscript
  // callbacks
  onSubmitTranscript,         // (finalText, buildSystemPrompt) => void|Promise — consumer's reply phase
  onVoiceCaptureStart,        // optional () => void — fired at the top of startCapture
}) {
  // 'idle' | 'consent' | 'denied' | 'device-in-use' | 'not-entitled' (A5: no premium
  // entitlement) | 'no-balance' (A5: voice-minutes balance exhausted) | 'other-tab-active'
  // (A5: another tab's activeVoiceSession marker is still fresh) | 'starting' | 'capturing' |
  // 'processing' | 'reviewing' (A4: transcript pending edit/confirm, see pendingVoiceText below) |
  // 'replying' (A3: awaiting Sonnet's reply) | 'speaking' (A3: Babi's TTS audio is playing) |
  // 'error'. All of the non-capture states are transient turn-level outcomes — none of them touch
  // the persisted consent record (profile.voiceConsent), only hasVoiceConsent does.
  const [voiceStage, setVoiceStage] = useState("idle");
  // 'none' (no attempt yet) | 'empty' (a definitively empty result — STT recognized no words, a
  // VAD misfire, or the max-silent-start timeout) — kept as its own explicit state rather than
  // relying on a transcript string's truthiness, which previously hid the 'empty' case (an empty
  // string is falsy, so the transcript banner just silently never rendered, with zero feedback
  // that anything was even attempted). A real (non-empty) result no longer has a state here at
  // all as of A4 — it goes straight to pendingVoiceText below instead of a separate display.
  const [transcriptState, setTranscriptState] = useState("none");
  // A4: a just-transcribed voice turn, editable, awaiting auto-send or explicit confirm/discard.
  // null when there's nothing pending. Deliberately NOT part of the real `messages` array — that
  // array is what gets sent to Sonnet as conversation history, and an unconfirmed draft must
  // never accidentally end up in it.
  const [pendingVoiceText, setPendingVoiceText] = useState(null);
  // Bumped on every new pending review (not on every edit keystroke) so the grace-period
  // progress-bar element can key off it to force a clean remount/restart per turn.
  const [pendingReviewId, setPendingReviewId] = useState(0);
  // Purely cosmetic (pauses the progress-bar animation) — the actual cancellation guarantee
  // below never depends on this state being set yet, only on the ref-based timer clear.
  const [pendingVoiceEditing, setPendingVoiceEditing] = useState(false);
  const pendingVoiceTimerRef = useRef(null);
  // null, or { type: 'unavailable' | 'unavailable-language' | 'tap-to-play' | 'not-entitled'
  // | 'no-balance', audio? } — a notice about Babi's TTS *reply* (A3), a separate concern
  // from transcriptState above (which is about what A2 heard, not what Babi says back). The
  // last two are the rare server-side voice-gate refusal on the TTS call specifically (the
  // STT-call equivalent routes through voiceStage in finishCapture instead).
  const [ttsNotice, setTtsNotice] = useState(null);
  const vadRef = useRef(null);
  const hasSpeechStartedRef = useRef(false);
  const silentStartTimerRef = useRef(null);
  // Holds a Promise<MicVAD> built ahead of time with startOnLoad:false — MicVAD.new()
  // compiles/loads the ~15-25MB ONNX Runtime WASM + Silero model before it ever touches
  // the microphone (confirmed from the library's own source: that load happens
  // unconditionally in `static async new()`, before the startOnLoad check that decides
  // whether to call getUserMedia), so building this early with no real handlers wired up
  // yet carries no mic-access/privacy cost at all — startCapture below still calls
  // getUserMedia only when the user actually taps the mic. See preloadVad below.
  const preloadedVadRef = useRef(null);
  // Whether the CURRENT voice turn is still wanted. Set false by cancelTurn() (consumer's
  // "Back" / restart) and by the unmount cleanup — every async continuation past a point of
  // no return (STT resolved → review, grace-timer → submit, reply text → speak) checks this
  // before its side effects, so navigating away or restarting mid-turn doesn't leave the
  // STT→Claude→TTS pipeline running and Babi answering a sentence nobody's there to hear.
  // Re-armed by armTurn() / startCapture at the top of each new turn.
  const turnActiveRef = useRef(true);
  // Monotonic turn counter — the piece turnActiveRef (a plain bool) can't express. It's
  // bumped on every armTurn() AND every cancelTurn()/unmount, so it only ever moves forward.
  // A long-running continuation (await askClaude / textToSpeech / speechToText) snapshots it
  // before its await and, on resume, bails unless the snapshot still matches. This closes the
  // resurrection gap: cancelTurn() flips turnActiveRef false, but the NEXT session's armTurn()
  // flips it true again — a stale continuation re-checking only the bool would sail through
  // and speak/bill/clobber under the new session. The generation snapshot never matches after
  // an intervening arm-or-cancel, so the stale continuation stays dead.
  const turnIdRef = useRef(0);
  // The Audio element currently playing a spoken reply, so cancelTurn()/unmount can stop it
  // mid-playback (a bare `new Audio()` isn't in the DOM, so it keeps playing on its own).
  const currentAudioRef = useRef(null);
  // Stable identity token for this hook instance — compared against the module-level
  // sameTabMicHolder to tell "this instance holds the mic" from "another instance in this
  // tab does". A plain object (cheaper than a Symbol; identity is all that matters).
  const micLockToken = useRef(null);
  if (!micLockToken.current) micLockToken.current = {};
  // Support is checked once, not on every render — it can't change mid-session.
  const micSupported = useMemo(() => !!(typeof navigator !== "undefined" && navigator.mediaDevices && navigator.mediaDevices.getUserMedia), []);
  // The icon's active/inactive look is driven ONLY by this (consent-record) state —
  // a transient per-turn failure (denied/device-in-use/error below) never changes it,
  // so retrying after a transient failure doesn't route back through A1's consent copy.
  const hasVoiceConsent = hasValidVoiceConsent(profile);
  // A5: derived fresh from props every render, never cached in state — so a subscription/
  // entitlement change is reflected immediately. Safe to read from deep async closures for
  // gating checks (unlike accumulating a balance, these aren't written-to, only read).
  const voiceEntitled = hasVoiceEntitlement(profile, subscription, isAdminAccount, isSimpleViewer);
  const voiceLimitSeconds = getVoiceLimitSeconds(profile, subscription, isAdminAccount, isSimpleViewer);
  const voiceBalanceResolved = resolveVoiceBalance(profile, subscription);
  // Two independent pools: this cycle's monthly allowance (spent first), then the persistent
  // purchased pool. Math.max guards a tier downgrade that left usedSeconds above the new,
  // lower limit — the monthly portion floors at 0, it never eats into the purchased pool.
  const voiceMonthlyRemaining = Math.max(0, voiceLimitSeconds - voiceBalanceResolved.usedSeconds);
  const voiceRemainingSeconds = voiceMonthlyRemaining + voiceBalanceResolved.purchasedSeconds;
  const hasVoiceBalance = voiceRemainingSeconds > 0;

  // Bills real STT/TTS usage against the user's voice-minutes balance. Uses onUpdateProfile's
  // functional-update form (prevProfile => ...), NOT the derived voiceBalanceResolved above —
  // this can be called twice in one voice turn (STT right after transcription, TTS later after
  // playback ends) from closures fixed back when mic capture began, well before either call
  // happens. Reading the current render's resolved balance and writing a precomputed number
  // would silently lose whichever bill applied first once the second call overwrote it with a
  // stale base value — the functional form re-resolves against the true latest profile at the
  // moment each update actually applies, regardless of how stale the calling closure is.
  const decrementVoiceBalance = seconds => {
    if (!seconds) return;
    onUpdateProfile?.(prevProfile => {
      const resolved = resolveVoiceBalance(prevProfile, subscription);
      const limit = getVoiceLimitSeconds(prevProfile, subscription, isAdminAccount, isSimpleViewer);
      // Spend this cycle's monthly allowance first; only the overflow past the tier limit
      // (including the whole amount once the allowance is used up, or after a downgrade that
      // put usedSeconds above the new limit) draws down the persistent purchased pool.
      // purchasedSeconds is otherwise never touched here, so a bought pack carries across
      // cycles and is never refilled by the monthly reset.
      const roomInMonthly = Math.max(0, limit - resolved.usedSeconds);
      const toMonthly = Math.min(seconds, roomInMonthly);
      const overflow = seconds - toMonthly;
      return {
        voiceBalance: {
          usedSeconds: resolved.usedSeconds + toMonthly,
          cycleAnchor: resolved.cycleAnchor,
          purchasedSeconds: Math.max(0, resolved.purchasedSeconds - overflow),
        },
      };
    });
  };

  const classifyMicError = err => {
    // Logged because the UI only ever shows a generic denial/in-use message —
    // without this, a genuinely different failure (e.g. MicVAD's model/worklet
    // assets failing to load) would be indistinguishable from a real permission
    // denial from the outside.
    console.error("Babi voice: mic/VAD init failed:", err);
    // NotReadableError/TrackStartError: OS/browser reports the device is claimed by
    // another app or tab — a fundamentally different, non-retriable-by-permission
    // problem from the user having declined access, so it gets its own message
    // rather than folding into the generic denial-fallback copy.
    if (err?.name === "NotReadableError" || err?.name === "TrackStartError") setVoiceStage("device-in-use");
    else setVoiceStage("denied");
  };

  // Called directly from a click handler (never from an effect or a .then chain)
  // so the getUserMedia call stays inside the user-gesture stack — iOS Safari
  // silently ignores the permission request otherwise.
  const requestMic = async (persistOnGrant) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // This call only proves out the permission grant (A1's job) — release
      // immediately, then hand off into A2's real capture flow below rather
      // than parking in a "coming soon" placeholder.
      stream.getTracks().forEach(track => track.stop());
      if (persistOnGrant) {
        onUpdateProfile?.({ voiceConsent: { accepted: true, version: VOICE_CONSENT_VERSION, acceptedAt: new Date().toISOString() } });
      }
      startCapture();
    } catch (err) {
      console.error("Babi voice: initial mic grant failed:", err);
      // Covers both a first-time browser denial and a returning user whose
      // previously granted permission was revoked out-of-band since their
      // last visit — same fallback either way. The persisted consent record
      // (acceptance of our copy) is deliberately left untouched here: that's
      // a separate question from the browser's live permission state.
      setVoiceStage("denied");
    }
  };

  // MicVAD.new() (startOnLoad defaults true) calls getUserMedia itself and rejects
  // with the original error (NotAllowedError/NotReadableError/etc. intact) if it
  // fails — no separate getUserMedia call needed before this.
  // Fully releases a finished/aborted VAD instance (mic stream, AudioContext,
  // worklet) and clears the ref, plus the max-silent-start timer below. Nulling
  // the ref alone (the earlier bug here) does NOT stop the instance — its
  // internal audio graph keeps running and kept firing onSpeechEnd for every
  // subsequent noise blip, each one POSTing a near-empty audio segment to
  // /api/speech-to-text on repeat.
  const releaseVad = () => {
    clearTimeout(silentStartTimerRef.current);
    silentStartTimerRef.current = null;
    const vad = vadRef.current;
    vadRef.current = null;
    vad?.destroy().catch(() => {});
    // Same-tab mic single-flight (see sameTabMicHolder) — released here, the funnel every
    // capture-end path already goes through, alongside the cross-tab activeVoiceSession clear.
    if (sameTabMicHolder === micLockToken.current) sameTabMicHolder = null;
    // A5: scoped to the capture (mic-hardware) phase specifically, not the whole turn
    // including Sonnet/TTS afterward — the mic is the actual contended resource two tabs
    // can't both legitimately hold; once capture ends, later API calls have no equivalent
    // hardware conflict to guard against. Cleared unconditionally on every capture end,
    // not just successful ones, so a denial/error never leaves a stale marker behind.
    onUpdateProfile?.({ activeVoiceSession: null });
  };

  // Builds (but does not start) a MicVAD instance in the background, well before the
  // user taps the mic, so the WASM/model compile cost lands before the button is ever
  // pressed instead of on every tap. Handlers are no-ops here — startCapture below
  // rebinds them (vad-web reads onSpeechStart/onSpeechEnd/onVADMisfire off `.options`
  // at fire time, not once at construction, so overwriting them right before `.start()`
  // is safe) to that turn's real, current closures rather than whatever was captured
  // at preload time. A rejected preload (e.g. VAD asset fetch failed) just clears the
  // ref — startCapture's own MicVAD.new() fallback covers that case identically to
  // today's behavior, so a failed preload is invisible to the user.
  const preloadVad = () => {
    if (preloadedVadRef.current || vadRef.current) return;
    preloadedVadRef.current = MicVAD.new({
      baseAssetPath: "/vad/",
      onnxWASMBasePath: "/vad/",
      redemptionMs: silenceTimeoutMs,
      preSpeechPadMs: PRE_SPEECH_PAD_MS,
      submitUserSpeechOnPause: true,
      startOnLoad: false,
      onSpeechStart: () => {},
      onSpeechEnd: () => {},
      onVADMisfire: () => {},
    }).catch(() => { preloadedVadRef.current = null; });
  };

  // Shared by the max-silent-start timeout and onVADMisfire below — both are a
  // "definitely nothing usable came of this turn" outcome, distinct from a real
  // STT failure (voiceStage stays 'idle', not 'error') and no STT call is made.
  const markEmptyResult = () => {
    releaseVad();
    setTranscriptState("empty");
    setVoiceStage("idle");
  };

  // Re-arm for a new turn — called by startCapture, and by the consumer at the start of a
  // fresh session (the Lab arms it when a topic is picked, since restart() disarmed it).
  const armTurn = () => { turnActiveRef.current = true; turnIdRef.current += 1; };
  const isTurnActive = () => turnActiveRef.current;

  // Abandon the in-flight turn: stop any playing reply audio, kill both timers, tear down
  // the VAD + mic lock, and mark the turn dead so the STT/Claude/TTS continuations below
  // bail. The consumer calls this from its "Back"/restart path (which does NOT unmount, so
  // the unmount effect can't cover it); the unmount effect does the same teardown inline.
  const cancelTurn = () => {
    turnActiveRef.current = false;
    turnIdRef.current += 1; // any continuation snapshotted under the old id is now stale
    currentAudioRef.current?.pause();
    currentAudioRef.current = null;
    clearTimeout(pendingVoiceTimerRef.current);
    pendingVoiceTimerRef.current = null;
    releaseVad(); // clears silentStartTimer, destroys VAD, releases the mic lock + activeVoiceSession
    setPendingVoiceText(null);
    setPendingVoiceEditing(false);
    setTranscriptState("none");
    setTtsNotice(null);
    setVoiceStage("idle");
  };

  // Clears the pending grace-period timer. The one function every cancellation path
  // (auto-fire, explicit confirm, discard, and — critically — the start of an edit)
  // funnels through, so there's exactly one place this logic lives.
  const cancelPendingVoiceTimer = () => {
    clearTimeout(pendingVoiceTimerRef.current);
    pendingVoiceTimerRef.current = null;
  };

  // Starts the A4 review window for a freshly-transcribed voice turn. Does NOT call
  // send() itself — send() only fires once this resolves, either via the grace-period
  // timeout below (untouched) or an explicit confirm (possibly edited).
  //
  // Defensively cancels any timer already running before scheduling a new one — found while
  // investigating a report that the review box could disappear in under 5 seconds: without this,
  // if this function ever fired twice in a row for any reason (e.g. a duplicate onSpeechEnd), the
  // FIRST call's timer would keep counting down from ITS OWN start time in the background (never
  // cleared) while the SECOND review is the one actually on screen — so the orphaned first timer
  // could fire and confirm/send early, well before the visibly-displayed review's own 5 seconds
  // had elapsed. Mirrors the same defensive cancel already done in startCapture.
  const startPendingVoiceReview = text => {
    cancelPendingVoiceTimer();
    const myTurn = turnIdRef.current;
    setPendingVoiceText(text);
    setPendingVoiceEditing(false);
    setPendingReviewId(id => id + 1);
    setVoiceStage("reviewing");
    // Lab: no grace-period auto-send — the turn stays in review until the learner explicitly
    // confirms (or redoes). The widget passes autoSendPendingReview:true (the default) and
    // reaches the setTimeout below exactly as before.
    if (!autoSendPendingReview) return;
    pendingVoiceTimerRef.current = setTimeout(() => {
      pendingVoiceTimerRef.current = null;
      // cancelTurn() already clears this timer, so a plain cancel can't reach here; the
      // generation guard is the belt-and-braces against a cancel+re-arm landing in the exact
      // window before this fires. Reaching this callback at all also proves editing never
      // began for this turn (see beginEditingPendingVoiceText) — using the original `text`
      // captured in this closure, not a re-read of state, is intentional.
      if (turnIdRef.current !== myTurn) return;
      confirmPendingVoiceText(text);
    }, TRANSCRIPT_GRACE_PERIOD_MS);
  };

  // The atomic-cancellation guarantee lives here: clearing the timer is the literal
  // first statement, run synchronously the instant the input fires (React dispatches
  // DOM events synchronously into handlers; nothing else can interleave mid-handler).
  // JS's single-threaded run-to-completion model makes this a hard guarantee, not a
  // "usually wins" race: either the setTimeout callback above was already dequeued and
  // running before this handler got a chance to run at all (in which case the turn was
  // already sent before the user's edit was even processed — an unavoidable, correct
  // outcome, not a bug), or this handler runs first and the timer is cancelled before
  // any other code in this function body executes, permanently, for this pending turn.
  // Bound to both onFocus (tapping in, before any keystroke) and onChange (belt-and-
  // braces against any input-method/browser event-ordering quirk) — either is
  // sufficient alone, both together cost nothing.
  const beginEditingPendingVoiceText = () => {
    cancelPendingVoiceTimer();
    setPendingVoiceEditing(true);
  };

  // Shared by the grace-period timeout, the explicit "send now" button, and the
  // edit field's own confirm action — always the single place a pending review turns
  // into a real send() call.
  const confirmPendingVoiceText = text => {
    cancelPendingVoiceTimer();
    // Turn abandoned (navigated away / restarted) while the grace timer was running, or
    // while the STT that scheduled it was still in flight — don't hand a transcript to the
    // consumer's reply phase for a session nobody's in.
    if (!turnActiveRef.current) return;
    setPendingVoiceText(null);
    setPendingVoiceEditing(false);
    const finalText = (text || "").trim();
    if (finalText) onSubmitTranscript?.(finalText, buildSystemPrompt);
    else setVoiceStage("idle");
  };

  const discardPendingVoiceText = () => {
    cancelPendingVoiceTimer();
    setPendingVoiceText(null);
    setPendingVoiceEditing(false);
    setVoiceStage("idle");
  };

  // Lab-only "cancel and redo": drop the pending transcript and immediately re-open the mic for
  // another attempt, in the same click handler so getUserMedia stays inside the user-gesture
  // stack (iOS Safari requirement). The STT already spent on the discarded transcript stays
  // billed — finishCapture bills before the review window ever opens — consistent with
  // discardPendingVoiceText. startCapture() re-arms the turn and re-acquires the mic lock (the
  // prior capture's onSpeechEnd already released it via releaseVad). The widget never calls
  // this — it's not in the widget's destructure — so it's inert there.
  const redoPendingVoiceText = () => {
    cancelPendingVoiceTimer();
    setPendingVoiceText(null);
    setPendingVoiceEditing(false);
    startCapture();
  };

  const startCapture = async () => {
    armTurn(); // a fresh turn is beginning — re-arm the cancel guard
    const myTurn = turnIdRef.current; // snapshot for the warm-up-hold guard below
    // Same-tab single-flight: acquire synchronously here (not just the advisory check in
    // handleMicClick) so a check→await gap between two instances can't both proceed. Also
    // covers the consent path (requestMic → startCapture), which skips handleMicClick.
    if (sameTabMicHolder && sameTabMicHolder !== micLockToken.current) { setVoiceStage("device-in-use"); return; }
    sameTabMicHolder = micLockToken.current;
    // 'starting' (loading the VAD's WASM/model assets and awaiting the browser's
    // permission prompt) is deliberately distinct from 'capturing' (actually
    // listening) — MicVAD.new() takes a few seconds before the permission prompt
    // even appears, and showing "Listening…" during that gap would be misleading.
    setVoiceStage("starting");
    setTranscriptState("none");
    setTtsNotice(null);
    // Consumer hook for any turn-scoped UI it wants reset when a new capture begins
    // (the widget clears its low-balance wrap-up nudge here).
    onVoiceCaptureStart?.();
    // Defensive — the mic is disabled during 'reviewing' so this shouldn't normally be
    // reachable with a pending review still outstanding, but never start a fresh capture
    // while a previous one is still silently in flight.
    cancelPendingVoiceTimer();
    setPendingVoiceText(null);
    setPendingVoiceEditing(false);
    hasSpeechStartedRef.current = false;
    const handlers = {
      onSpeechStart: () => {
        hasSpeechStartedRef.current = true;
        clearTimeout(silentStartTimerRef.current);
      },
      onSpeechEnd: audio => {
        releaseVad();
        finishCapture(audio);
      },
      onVADMisfire: () => {
        // Speech-shaped noise too brief to count (e.g. a cough) — a
        // definitively empty result, not silently back to idle with no
        // feedback (the earlier bug here).
        markEmptyResult();
      },
    };
    try {
      // Reuse a background-preloaded instance when one's ready — it already paid the
      // WASM/model compile cost, so only `.start()` (getUserMedia + AudioContext +
      // AudioWorklet setup) remains on the critical path here. Its placeholder handlers
      // from preloadVad get overwritten with this turn's real, current closures first —
      // vad-web reads them off `.options` at fire time, not once at construction, so
      // this is safe even though the instance was built earlier in the component's life.
      const preload = preloadedVadRef.current;
      preloadedVadRef.current = null;
      let vad = null;
      if (preload) {
        try {
          vad = await preload;
          Object.assign(vad.options, handlers);
          await vad.start();
        } catch {
          vad = null; // preload failed — fall through to building fresh, below
        }
      }
      if (!vad) {
        vad = await MicVAD.new({
          // Self-hosted, not the library's documented CDN default — CRA's webpack
          // intercepts onnxruntime-web's internal dynamic import() as a code-split
          // chunk request instead of a runtime CDN fetch, so the CDN default never
          // actually loads under this bundler. Files copied into public/vad/ by
          // scripts/copy-vad-assets.js (runs via postinstall).
          baseAssetPath: "/vad/",
          onnxWASMBasePath: "/vad/",
          redemptionMs: silenceTimeoutMs,
          preSpeechPadMs: PRE_SPEECH_PAD_MS, // keep more lead-in so an early first word isn't clipped
          submitUserSpeechOnPause: true, // lets the manual-stop button submit whatever's captured so far
          ...handlers,
        });
      }
      vadRef.current = vad;
      // Warm-up hold — the VAD is already listening/buffering, but hold "starting…" a beat so
      // "Listening…" only shows once the freshly-opened mic has settled (see MIC_WARMUP_MS).
      await new Promise(r => setTimeout(r, MIC_WARMUP_MS));
      // Bailed during the hold: stop button (→ markEmptyResult → releaseVad nulls vadRef),
      // Back/navigate (→ cancelTurn bumps turnIdRef), or unmount. Don't announce "Listening…".
      if (vadRef.current !== vad || turnIdRef.current !== myTurn || !turnActiveRef.current) return;
      setVoiceStage("capturing"); // getUserMedia + model init done, mic warm — actually listening now
      // A5: claims the mic for this tab. Plain object update, not the functional form
      // decrementVoiceBalance uses — this isn't an accumulating value, just an overwrite,
      // so there's no equivalent staleness risk.
      onUpdateProfile?.({ activeVoiceSession: { tabId: VOICE_SESSION_TAB_ID, startedAt: new Date().toISOString() } });
      // Independent of redemptionMs (see MAX_SILENT_START_MS's own comment) —
      // covers the case where speech never starts at all, which the library's
      // own silence logic has no concept of.
      silentStartTimerRef.current = setTimeout(() => {
        if (!hasSpeechStartedRef.current) markEmptyResult();
      }, MAX_SILENT_START_MS);
    } catch (err) {
      vadRef.current = null;
      // The one capture-end path that doesn't route through releaseVad (vadRef was never
      // set) — release the same-tab lock here too so a failed init doesn't wedge it.
      if (sameTabMicHolder === micLockToken.current) sameTabMicHolder = null;
      classifyMicError(err);
    }
  };

  // Manual stop affordance, and also what the page-visibility handler below calls.
  const stopCapture = () => {
    if (!hasSpeechStartedRef.current) {
      // Confirmed from the library's own endSegment() logic: pause() before any
      // speech was ever detected fires neither onSpeechEnd nor onVADMisfire
      // (both are gated behind `speaking`, which was never set true) — relying
      // on pause() here would silently leave the instance paused-but-undestroyed
      // and the UI stuck on "capturing" forever. Handle it directly instead, the
      // same outcome as the max-silent-start timeout.
      markEmptyResult();
      return;
    }
    // Speech already started: pause() triggers submitUserSpeechOnPause, which
    // reliably fires onSpeechEnd/onVADMisfire — cleanup happens there, not here.
    vadRef.current?.pause();
  };

  const finishCapture = async audio => {
    setVoiceStage("processing");
    const myTurn = turnIdRef.current;
    try {
      const langCode = languageCode || LANGUAGE_NAME_TO_BCP47[profile?.baseLanguage] || "en-US";
      const data = await speechToText(floatTo16BitPCMBase64(audio), 16000, langCode);
      const text = data.transcript || "";
      // A5: billed immediately, before the pending-review flow starts, and regardless of
      // whether the result is empty — real Google cost was incurred either way, the moment
      // this call succeeded. Billing here rather than later means the discard path (A4)
      // needs no special-casing at all: by the time a discard is even possible, the STT
      // portion has already been billed.
      decrementVoiceBalance(parseGoogleDurationSeconds(data.billedDuration));
      // Turn abandoned (navigated away / restarted) while this STT call was in flight — the
      // Google cost was still real so it's billed above, but nothing downstream should run:
      // no review window, no auto-send, no reply. (Guarded here rather than only at unmount
      // because startPendingVoiceReview would otherwise schedule a fresh timer AFTER the
      // unmount cleanup already cleared the old one.) The turnIdRef half also catches the
      // case where a NEW session was armed while this call was in flight — turnActiveRef is
      // true again then, but the generation has moved on.
      if (!turnActiveRef.current || turnIdRef.current !== myTurn) return;
      // A4: a real transcript goes into the editable review window (auto-sends after
      // TRANSCRIPT_GRACE_PERIOD_MS unless edited) rather than straight to send() — see
      // startPendingVoiceReview. An empty result never reaches Sonnet at all — nothing
      // to send, and nothing to review either.
      if (text) startPendingVoiceReview(text);
      else { setTranscriptState("empty"); setVoiceStage("idle"); }
    } catch (err) {
      // STT itself failed (network/5xx after api.js's one retry) — no billedDuration was
      // ever returned, so nothing gets billed here. Unambiguous, inspectable failure
      // outcome for A5 to key off — a failed turn must never look the same as a successful
      // one (this was already true before A5; A5 is what actually acts on it now).
      //
      // A server-side voice-gate refusal (402/403 from the proxy) is a rare fallback: the
      // mic button pre-checks entitlement+balance in handleMicClick, so this only fires when
      // the balance ran out or entitlement lapsed server-side after that check — another
      // tab/device spent it, or a subscription expired mid-session. Route it to the same two
      // states the pre-check uses so the user sees the real reason, not a generic error.
      if (err?.code === "voice-not-entitled") setVoiceStage("not-entitled");
      else if (err?.code === "voice-no-balance") setVoiceStage("no-balance");
      else setVoiceStage("error");
    }
  };

  // Synthesizes and plays Babi's text reply — called only for voice turns. Leaves
  // voiceStage in 'speaking' on entry; does NOT reset it back to 'idle' itself (send()'s
  // finally block is the single place that happens, so this and a plain STT-only path
  // can't race to set conflicting final states).
  // Returns { audioContent, rate } on success (the synthesized base64 mp3 plus the speakingRate
  // it was generated at), null otherwise — so a caller can cache both for a later "Listen again"
  // replay and correct its playback speed against a toggle value that's since changed (see
  // ListenSpeakLab's playTurnAudio). This is purely a courtesy return of audio that was already
  // paid for and generated regardless (Google bills per synthesis call, not per playback — see
  // the 'ended' handler's comment below); a later replay from this cached string plays a fresh
  // local `Audio` with no billing listener attached, so it never re-bills voice minutes, matching
  // the product decision that a local re-listen has no extra cost.
  const speakReply = async text => {
    // Consumers that don't want spoken replies (the chat widget today) already gate the
    // call site on their own flag and never reach here — this guard makes `ttsEnabled` a
    // real contract for consumers that hand the reply phase to the hook instead.
    if (!ttsEnabled || !turnActiveRef.current) return null;
    const myTurn = turnIdRef.current;
    setVoiceStage("speaking");
    try {
      const langCode = languageCode || LANGUAGE_NAME_TO_BCP47[profile?.baseLanguage] || "en-US";
      const data = await textToSpeech(text, langCode, ttsVoice, ttsRate);
      // Turn abandoned while TTS was synthesizing — don't start playback (a bare `new Audio`
      // plays even after the component unmounts, so this must be checked before .play()). The
      // turnIdRef half catches a NEW session armed during synthesis (turnActiveRef true again,
      // generation moved on) — otherwise the abandoned reply would be spoken and billed.
      if (!turnActiveRef.current || turnIdRef.current !== myTurn) return null;
      const audio = new Audio(`data:audio/mp3;base64,${data.audioContent}`);
      currentAudioRef.current = audio; // so cancelTurn()/unmount can .pause() it mid-playback
      await new Promise(resolve => {
        audio.addEventListener("ended", () => {
          currentAudioRef.current = null;
          // A5/A6: TTS is billed here specifically — only when playback actually completed,
          // not merely synthesized. audio.duration is reliably populated by the time
          // 'ended' fires (the browser necessarily parsed the file to play it). A blocked-
          // autoplay turn the user never taps to hear at all is never billed (this listener
          // simply never fires) — Google was still paid to synthesize it, the user just never
          // heard it. CONFIRMED (2026-08-17, re-checked against actual behavior, corrected
          // from an earlier wrong assumption): a LATER manual "tap to hear" replay that
          // actually plays to completion DOES get billed here, once — this same {once:true}
          // listener, attached back when the blocked autoplay attempt was made, is still live
          // and fires normally the first time real playback finishes, regardless of how much
          // later that is. Confirmed intentional: bill for what's actually heard, whenever
          // that happens. {once:true} guarantees at most one bill per Audio instance either
          // way, so there's no double-billing risk from this.
          decrementVoiceBalance(Number.isFinite(audio.duration) ? audio.duration : 0);
          resolve();
        }, { once: true });
        audio.addEventListener("error", () => { currentAudioRef.current = null; resolve(); }, { once: true });
        // cancelTurn()/unmount pauses the audio — treat that as "done" so this promise
        // resolves instead of hanging a detached closure forever.
        audio.addEventListener("pause", () => { if (!turnActiveRef.current || turnIdRef.current !== myTurn) { currentAudioRef.current = null; resolve(); } }, { once: true });
        const playAttempt = audio.play();
        if (playAttempt?.catch) {
          playAttempt.catch(() => {
            // Autoplay blocked — expected on iOS Safari specifically, confirmed by
            // investigation: play() must be called synchronously within the original
            // gesture handler there, and this call happens well after the STT+Sonnet+TTS
            // round trip, i.e. asynchronously relative to the mic-tap gesture. A "silent
            // primer" workaround exists but was explicitly decided against (fragile,
            // undocumented WebKit behavior) — this plain affordance is the sole handling.
            // Not billed here — see the 'ended' handler's comment above.
            setTtsNotice({ type: "tap-to-play", audio });
            resolve();
          });
        }
      });
      return { audioContent: data.audioContent, rate: typeof ttsRate === "number" ? ttsRate : 1 };
    } catch (err) {
      // Rare fallback: a server-side voice-gate refusal (402/403) on the TTS call itself —
      // STT already succeeded and the reply text is on screen, only the spoken audio is
      // blocked. Carries `code` and NOT googleStatus/googleMessage (see api.js), so it can't
      // be confused with the no-voice-for-language case below.
      if (err?.code === "voice-not-entitled" || err?.code === "voice-no-balance") {
        setTtsNotice({ type: err.code === "voice-no-balance" ? "no-balance" : "not-entitled" });
        return null;
      }
      // Both conditions required together, not either alone — matching only the message
      // text is too fragile if Google ever rewords it, and matching the bare status is too
      // broad (other unrelated failures also return INVALID_ARGUMENT). Verified this exact
      // shape directly against the real API for a language with no voice (Farsi): status
      // "INVALID_ARGUMENT", message "Voice '' does not exist. Is it misspelled?".
      const isNoVoice = err.googleStatus === "INVALID_ARGUMENT" && (err.googleMessage || "").includes("does not exist");
      setTtsNotice({ type: isNoVoice ? "unavailable-language" : "unavailable" });
      return null;
    }
  };

  // Backgrounding the tab mid-capture ends the turn the same way a manual stop
  // does, rather than leaving the mic listening indefinitely while hidden.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden" && vadRef.current) stopCapture();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  // stopCapture only ever touches refs and setState functions (both stable across
  // renders), so the listener registered once at mount is never actually stale.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Release the mic (and any pending timer — max-silent-start or A4's grace-period
  // review timer) if the widget unmounts mid-capture/mid-review — otherwise a timer
  // would still fire later and try to update state on an unmounted component.
  useEffect(() => () => {
    // Mark the turn dead so any in-flight STT/Claude/TTS continuation bails, and stop a
    // spoken reply that's already playing (a bare `new Audio` keeps going after unmount).
    turnActiveRef.current = false;
    turnIdRef.current += 1; // keep the generation invariant (a stale snapshot never rematches)
    currentAudioRef.current?.pause();
    currentAudioRef.current = null;
    clearTimeout(silentStartTimerRef.current);
    clearTimeout(pendingVoiceTimerRef.current);
    vadRef.current?.destroy();
    // Unmounting mid-capture (e.g. navigating away from the Lab) — free the same-tab mic
    // lock so the other instance isn't left permanently blocked.
    if (sameTabMicHolder === micLockToken.current) sameTabMicHolder = null;
    // A never-started preload still holds the compiled WASM/model in memory —
    // release it too rather than just letting it leak until GC gets around to it.
    preloadedVadRef.current?.then(vad => vad?.destroy()).catch(() => {});
  }, []);

  // Warms the VAD (WASM/model compile) as soon as the panel opens, so the first mic
  // tap in a conversation isn't what pays that cost — see preloadVad's own comment.
  // Gated on consent/entitlement/balance: no point doing this multi-megabyte background
  // load for a user who can't actually use voice, or has never opted into it (a
  // first-time user still goes through the consent screen's own explicit tap first,
  // which then calls startCapture directly, not through this preload path at all).
  useEffect(() => {
    if (panelOpen && enablePreload && micSupported && hasVoiceConsent && voiceEntitled && hasVoiceBalance) preloadVad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOpen, enablePreload, micSupported, hasVoiceConsent, voiceEntitled, hasVoiceBalance]);

  const handleMicClick = () => {
    if (!micSupported) return;
    if (voiceStage === "capturing") { stopCapture(); return; }
    // 'starting': MicVAD.new() has no cancellation hook — vadRef isn't set until
    // it resolves, so there's nothing yet to stop. 'processing'/'replying'/'speaking':
    // one turn (including its Sonnet round trip and spoken reply) at a time.
    if (["starting", "processing", "reviewing", "replying", "speaking"].includes(voiceStage)) return;
    // A5 gates, checked before consent — no point asking for mic permission if the user
    // can't use the feature at all, or has nothing left to use it with this cycle.
    if (!voiceEntitled) { setVoiceStage("not-entitled"); return; }
    if (!hasVoiceBalance) { setVoiceStage("no-balance"); return; }
    // Scoped to the capture/mic-hardware phase only (see releaseVad's comment) — a stale
    // marker (crashed/closed tab that never got to clear it) is ignored past this staleness
    // window rather than permanently locking voice out for every other tab.
    const activeSession = profile?.activeVoiceSession;
    const sessionIsStale = !activeSession?.startedAt
      || (Date.now() - new Date(activeSession.startedAt).getTime()) > VOICE_SESSION_STALE_MS;
    if (activeSession && activeSession.tabId !== VOICE_SESSION_TAB_ID && !sessionIsStale) {
      setVoiceStage("other-tab-active");
      return;
    }
    // Same-tab: another useVoiceTurn instance in THIS tab already holds the mic (see
    // sameTabMicHolder). Advisory pre-check for a clean error — startCapture re-checks
    // synchronously to close the race.
    if (sameTabMicHolder && sameTabMicHolder !== micLockToken.current) { setVoiceStage("device-in-use"); return; }
    if (hasVoiceConsent) startCapture();
    else setVoiceStage("consent");
  };

  return {
    // state
    voiceStage,
    setVoiceStage,
    transcriptState,
    setTranscriptState,
    pendingVoiceText,
    setPendingVoiceText,
    pendingVoiceEditing,
    pendingReviewId,
    ttsNotice,
    setTtsNotice,
    // derived
    micSupported,
    hasVoiceConsent,
    voiceEntitled,
    hasVoiceBalance,
    voiceRemainingSeconds,
    // actions
    requestMic,
    handleMicClick,
    beginEditingPendingVoiceText,
    confirmPendingVoiceText,
    discardPendingVoiceText,
    redoPendingVoiceText, // Lab-only: discard the pending transcript + re-open the mic in one gesture
    speakReply,
    armTurn,        // consumer calls at the start of a fresh session (re-arms the cancel guard)
    cancelTurn,     // consumer calls from its "Back"/restart path — kills the in-flight turn
    isTurnActive,   // () => bool — consumer's reply phase checks this after its own awaits
  };
}
